import {
  type IObservableArray,
  action,
  computed,
  makeObservable,
  observable,
} from 'mobx'
import { type StaticModelCollection } from '../firestore-mobx/model'
import { getCatalogsForUser } from '../firestore/Catalog'
import { getUsers } from '../firestore/PublicUser'
import {
  SectionPaymentMethod,
  SectionState,
  batchUpdateSectionCollaborators,
  createSection,
  createSectionWithProduct,
  deleteSection,
  fetchSection,
  getSectionsById,
  getSectionsStreamForInstructor,
  getSectionsStreamForInstructors,
  setSectionState,
  updateSection,
} from '../firestore/Section'
import { createSectionProduct } from '../firestore/SectionProduct'
import { getSectionAssignments } from '../firestore/SectionAssignment'
import { getSectionPromotions } from '../firestore/SectionPromotion'
import { getAssistedInstructorIDs } from '../firestore/UserProfile'
import { deleteAssistantship } from '../firestore/UserProfileInstructor'
import {
  applyApprovedFreeAdminPromotion,
  redeemPromotions,
} from '../firestore/UserPromotionRedemption'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { PublicUser } from '../models/PublicUser'
import { Section, SectionStatus } from '../models/Section'
import type { BreakoutUser } from '../models/BreakoutUser'
import { SectionAssignment } from '../models/SectionAssignment'
import type { SectionPromotion } from '../models/SectionPromotion'
import type { UserPromotion } from '../models/UserPromotion'
import type {
  ImpersonationInfo,
  InvitationType,
  SectionWithStatusAndAssignments,
} from '../types'
import { UserProfileRole, SectionRequestState } from '../types'
import { Cubit } from './core'
import { captureException } from '@sentry/core'
import { SectionRequest } from '../models/SectionRequest'
import type { FirestoreSectionRequest } from '../firestore/SectionRequest'
import {
  createSectionInvoiceRequest,
  getSectionRequestsBySectionId,
  SectionRequestType,
} from '../firestore/SectionRequest'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { guardOn, guardOnLoaded } from '../util/guardOn'
import { DateTime } from 'luxon'
import { createStudentInvitation } from '../firestore/Invitation'
import { Organization } from '../models/Organization'
import { getOrganization } from '../firestore/Organization'
import { getSectionsStreamForCollaborator } from '../firestore/Section'

export type InstructorClassesSortingStrategy =
  | 'className'
  | 'sectionName'
  | 'students'
  | 'dates'
  | 'status'
  | 'instructor'
  | 'assignments'

export type InstructorClassesSortingOrder = 'asc' | 'desc'

export class InstructorClassesCubit extends Cubit {
  repository: FirebaseRepository
  userId: string
  role: string
  impersonatedBreakoutUser: BreakoutUser

  @observable sortingStrategy: InstructorClassesSortingStrategy = 'status'
  @observable sortingOrder: InstructorClassesSortingOrder = 'asc'

  sectionsFromBeingInstructor: StaticModelCollection<Section>
  sectionsFromUsersIAssist: StaticModelCollection<Section>
  sectionsFromBeingCollaborator: StaticModelCollection<Section>

  sharedSections: StaticModelCollection<Section>
  settingsSectionPassPricing: SettingsSectionPassPricing

  promotionsForSection = observable.map<string, SectionPromotion[]>()

  assignmentsForSection = observable.map<
    string,
    StaticModelCollection<SectionAssignment>
  >()

  invoiceRequestsBySectionId = observable.map<
    string,
    StaticModelCollection<SectionRequest>
  >()

  organizationsBySectionId = observable.map<string, Organization | null>()

  private _sharedSectionIds: IObservableArray<string> | undefined

  instructorUser: PublicUser
  impersonationInfo?: ImpersonationInfo

  fallbackSectionPassPrice: SettingsSectionPassPricing

  constructor(
    repository: FirebaseRepository,
    {
      role,
      impersonationInfo,
      impersonatedBreakoutUser,
    }: {
      role: string
      impersonationInfo?: ImpersonationInfo
      impersonatedBreakoutUser: BreakoutUser
    }
  ) {
    super()
    makeObservable(this)
    this.impersonationInfo = impersonationInfo

    this.userId = repository.uid
    this.repository = repository
    this.role = role

    this.impersonatedBreakoutUser = impersonatedBreakoutUser

    this.sectionsFromBeingInstructor = Section.emptyCollection(repository)
    this.sectionsFromUsersIAssist = Section.emptyCollection(repository)
    this.sectionsFromBeingCollaborator = Section.emptyCollection(repository)
    this.sharedSections = Section.emptyCollection(repository)
    this.instructorUser = PublicUser.empty(repository)
    this.settingsSectionPassPricing =
      SettingsSectionPassPricing.empty(repository)
    this.fallbackSectionPassPrice = SettingsSectionPassPricing.empty(repository)
  }

  initialize(): void {
    this.addStream(
      getSettingsSectionPassPricing(this.repository),
      (settings) => {
        this.settingsSectionPassPricing.replaceModel(settings)
      },
      { name: 'settings-section-pass-pricing' }
    )

    this.addStream(
      getSectionsStreamForCollaborator(this.repository, this.repository.uid),
      (sections) => {
        this.sectionsFromBeingCollaborator.replaceModels(sections)

        sections.forEach((section) => {
          this.ensureStreamsForSection(section)
        })
      }
    )

    this.addStream(
      getAssistedInstructorIDs(
        this.repository,
        this.impersonatedBreakoutUser.uid
      ),
      (instructorIds) => {
        const sectionsStreamLabel = 'ta-sections-stream'
        this.removeStream(sectionsStreamLabel)

        if (instructorIds.length === 0) {
          this.sectionsFromUsersIAssist.replaceModels([])
          return
        }

        this.addStream(
          getSectionsStreamForInstructors(this.repository, instructorIds),
          (sections) => {
            this.sectionsFromUsersIAssist.replaceModels(sections)

            sections.forEach((section) => {
              this.ensureStreamsForSection(section)
            })
          },
          { name: sectionsStreamLabel }
        )
      }
    )

    this.addStream(
      getSectionsStreamForInstructor(this.repository, this.impersonationInfo),
      (sections) => {
        this.sectionsFromBeingInstructor.replaceModels(sections)
        this.startSectionPromotionStreams(sections)

        sections.forEach((section) => {
          this.ensureStreamsForSection(section)
        })
      },
      { name: 'sections' }
    )

    if (this.role === 'instructor') {
      this.addStream(
        getCatalogsForUser(this.repository, {
          userId: this.impersonationInfo?.userId ?? this.userId,
        }),
        (catalogs) => {
          const sharedSectionStreamKey = 'shared-sections-stream'
          const sharedSectionIds = Array.from(
            new Set(
              catalogs.flatMap(
                ({ catalog }) => catalog.data.catalogSharedSectionIds
              )
            )
          )
          if (
            this._sharedSectionIds &&
            this.arraysEqual(sharedSectionIds, this._sharedSectionIds)
          ) {
            return
          }
          this._sharedSectionIds = observable.array(sharedSectionIds)
          this.removeStream(sharedSectionStreamKey)
          this.addStream(
            getSectionsById(this.repository, sharedSectionIds),
            (sharedSections) => {
              sharedSections.forEach((section) => {
                this.ensureStreamsForSection(section)
              })
              this.sharedSections.replaceModels(sharedSections)
            },
            {
              name: sharedSectionStreamKey,
            }
          )
        },
        { name: 'catalogs' }
      )
    } else {
      // if not an instructor, change loading state for sharedSections to false
      // we don't care for non-instructors
      this.sharedSections.changeLoadingState(false)
    }

    // if we are impersonating an instructor, get the instructor's profile
    if (this.impersonationInfo?.userId) {
      this.addStream(
        getUsers(this.repository, {
          userIds: [this.impersonationInfo?.userId],
        }),
        (instructors) => {
          this.instructorUser.replaceModel(instructors[0])
          return
        },
        { name: 'instructor-profile' }
      )
    }

    this.addStream(getSettingsSectionPassPricing(this.repository), (pricing) =>
      this.fallbackSectionPassPrice.replaceModel(
        pricing.hasData
          ? pricing
          : SettingsSectionPassPricing.empty(this.repository)
      )
    )
  }

  @action
  ensureStreamsForSection(section: Section) {
    const key1 = `section-stream-${section.id}-assignments`
    if (!this.hasStream(key1)) {
      this.assignmentsForSection.set(
        section.id,
        SectionAssignment.emptyCollection(this.repository)
      )
      this.startAssignmentStream(section, 0, key1)
    }

    const key2 = `section-stream-${section.id}-requests`
    if (!this.hasStream(key2)) {
      this.invoiceRequestsBySectionId.set(
        section.id,
        SectionRequest.emptyCollection(this.repository)
      )
      if (section.data.organizationId) {
        this.startSectionRequestStream(section, 0, key2)
      }
    }

    const key3 = `section-stream-${section.id}-organization`
    if (!this.hasStream(key3)) {
      this.organizationsBySectionId.set(
        section.id,
        Organization.empty(this.repository)
      )
      if (section.data.organizationId) {
        this.startOrganizationStream(section)
      }
    }
  }

  @action
  startAssignmentStream(section: Section, retryCount = 0, name?: string) {
    this.addStream(
      getSectionAssignments(this.repository, { sectionId: section.id }),
      (assignments) => {
        this.assignmentsForSection.get(section.id)?.replaceModels(assignments)
      },
      {
        name: name ?? `section-assignments-${section.id}`,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startAssignmentStream(section, retryCount + 1, name)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  @action
  startSectionRequestStream(section: Section, retryCount = 0, name?: string) {
    this.addStream(
      getSectionRequestsBySectionId(this.repository, section.id),
      (requests) => {
        const sortedRequests = requests.sort((a, b) => {
          return b.data.requestedAt.getTime() - a.data.requestedAt.getTime()
        })

        const invoiceRequests = sortedRequests.filter(
          (request) =>
            request.data.sectionRequestType === SectionRequestType.invoice
        )

        this.invoiceRequestsBySectionId
          .get(section.id)!
          .replaceModels(invoiceRequests)
      },
      {
        name: name ?? `section-requests-${section.id}`,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startSectionRequestStream(section, retryCount + 1, name)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  @action
  startOrganizationStream(section: Section) {
    this.addStream(
      getOrganization(this.repository, {
        organizationId: section.data.organizationId!,
      }),
      (organization) => {
        this.organizationsBySectionId.set(section.id, organization)
      },
      {
        name: `organization-${section.id}`,
        disableCaptureException: true,
        onError: (error) => {
          captureException(error)
        },
      }
    )
  }

  /** @deprecated */
  isSharedSection = (section: Section) => {
    return this._sharedSectionIds?.includes(section.id) ?? false
  }

  fetchSection(sectionId: string) {
    return fetchSection(this.repository, {
      sectionId,
    })
  }

  @computed
  get mySections() {
    return guardOnLoaded(
      [
        this.sectionsFromBeingInstructor,
        this.sectionsFromUsersIAssist,
        this.sectionsFromBeingCollaborator,
        this.impersonatedBreakoutUser.usersIAssist,
        this.impersonatedBreakoutUser.usersICollaborateWith,
      ] as const,
      ([s1, s2, s3]) => {
        const allSections = [...s1, ...s2, ...s3]
        const uniqueSectionIds = new Set<string>()

        const uniqueSections = allSections.filter((section) => {
          if (uniqueSectionIds.has(section.id)) {
            return false
          }
          uniqueSectionIds.add(section.id)
          return true
        })

        return uniqueSections
      }
    )
  }

  @computed
  get sampleSections() {
    return guardOn(this.sharedSections.isLoaded, () => {
      return this.sharedSections.models
    })
  }

  @computed
  get sectionsWithStatusAndAssignments() {
    return guardOn(
      this.mySections.isLoaded &&
        this.mySections.value.every(
          (section) =>
            this.assignmentsForSection.get(section.id) &&
            this.assignmentsForSection.get(section.id)!.isLoaded &&
            (!section.data.organizationId ||
              (this.invoiceRequestsBySectionId.get(section.id) &&
                this.invoiceRequestsBySectionId.get(section.id)!.isLoaded))
        ),
      () => {
        const mySections = this.mySections.value!

        const sections: SectionWithStatusAndAssignments[] = mySections.map(
          (section) => {
            const assignments = this.assignmentsForSection.get(
              section.id
            )!.models

            const latestInvoiceRequest = this.invoiceRequestsBySectionId.get(
              section.id
            )!.models[0]

            const sectionStatus = section.getSectionStatus({
              assignments,
              latestInvoiceRequest,
            })

            return {
              section,
              sectionStatus,
              assignments,
            }
          }
        )

        sections.sort(
          (
            a: SectionWithStatusAndAssignments,
            b: SectionWithStatusAndAssignments
          ) => {
            let comparison = 0
            switch (this.sortingStrategy) {
              case 'className':
                comparison = a.section.data.className.localeCompare(
                  b.section.data.className
                )
                break
              case 'sectionName':
                comparison = a.section.data.sectionName.localeCompare(
                  b.section.data.sectionName
                )
                break
              case 'students':
                comparison = a.section.userCount - b.section.userCount
                break
              case 'assignments':
                comparison = a.assignments.length - b.assignments.length
                break
              case 'dates': {
                const aDate =
                  a.section.data.sectionStartDate ??
                  a.section.data.sectionEndDate
                const bDate =
                  b.section.data.sectionStartDate ??
                  b.section.data.sectionEndDate

                if (!aDate && !bDate) {
                  comparison = 0
                } else if (!aDate) {
                  comparison = -1
                } else if (!bDate) {
                  comparison = 1
                } else {
                  const aStart = DateTime.fromJSDate(aDate).startOf('day')
                  const bStart = DateTime.fromJSDate(bDate).startOf('day')
                  comparison = aStart.diff(bStart).milliseconds
                }
                break
              }
              case 'status': {
                const statusOrder = {
                  [SectionStatus.draft]: 0,
                  [SectionStatus.invoiceRejected]: 1,
                  [SectionStatus.invoicePending]: 2,
                  [SectionStatus.notStarted]: 3,
                  [SectionStatus.inProgress]: 4,
                  [SectionStatus.completed]: 5,
                }
                comparison =
                  statusOrder[a.sectionStatus] - statusOrder[b.sectionStatus]
                break
              }
              case 'instructor': {
                const instructorA =
                  this.impersonatedBreakoutUser.usersIAssist.value!.find(
                    (i) => i.id === a.section.data.instructorUserId
                  )
                const instructorB =
                  this.impersonatedBreakoutUser.usersIAssist.value!.find(
                    (i) => i.id === b.section.data.instructorUserId
                  )
                const nameA = instructorA
                  ? `${instructorA.data.firstName} ${instructorA.data.lastName}`
                  : ''
                const nameB = instructorB
                  ? `${instructorB.data.firstName} ${instructorB.data.lastName}`
                  : ''
                comparison = nameA.localeCompare(nameB)
                break
              }
            }
            return this.sortingOrder === 'asc' ? comparison : -comparison
          }
        )

        return sections
      }
    )
  }

  @computed
  get allowSectionPassPaymentMethod() {
    return (
      this.settingsSectionPassPricing.isLoaded &&
      this.settingsSectionPassPricing.hasData
    )
  }

  invoiceRequestsForSectionId(sectionId: string) {
    return guardOn(
      this.mySections.isLoaded &&
        this.mySections.value.some(
          (section) =>
            !section.data.organizationId ||
            this.invoiceRequestsBySectionId.get(section.id)?.isLoaded
        ),
      () => {
        return this.invoiceRequestsBySectionId.get(sectionId)!
      }
    )
  }

  organizationForSectionId(sectionId: string) {
    return guardOn(
      this.mySections.isLoaded &&
        this.mySections.value.some(
          (section) =>
            section.id === sectionId &&
            (!section.data.organizationId ||
              this.organizationsBySectionId.get(section.id)?.isLoaded)
        ),
      () => {
        return this.organizationsBySectionId.get(sectionId)
      }
    )
  }

  createSectionInvoiceRequest = async ({
    sectionId,
    sectionRequest,
  }: {
    sectionId: string
    sectionRequest: Pick<
      FirestoreSectionRequest,
      | 'sectionRequestAssignmentCount'
      | 'sectionRequestStudentCount'
      | 'requestedAt'
      | 'organizationId'
      | 'sectionId'
      | 'updatedByUserId'
    >
  }) => {
    await createSectionInvoiceRequest(this.repository, sectionId, {
      ...sectionRequest,
      userId: this.repository.uid,
      sectionRequestType: SectionRequestType.invoice,
      sectionRequestState: SectionRequestState.pending,
      updatedAt: new Date(),
      requestedAt: new Date(),
    })
  }

  createSection = async ({
    className,
    sectionName,
    organizationId,
    impersonationInfo,
    sectionPaymentMethod,
    expectedStudents,
    sectionStartDate,
    sectionEndDate,
    selectedProduct,
  }: {
    className: string
    sectionName: string
    organizationId?: string
    impersonationInfo?: ImpersonationInfo
    sectionPaymentMethod?: SectionPaymentMethod
    expectedStudents?: number
    sectionStartDate?: DateTime
    sectionEndDate?: DateTime
    selectedProduct?: { productId: string; isCustom: boolean }
  }) => {
    if (this.role === UserProfileRole.ta && !impersonationInfo?.userId) {
      throw new Error('instructorUserId is required for TA to create a section')
    }

    const baseParams = {
      className,
      sectionName,
      userId: impersonationInfo?.userId ?? this.userId,
      organizationId,
      sectionPaymentMethod: sectionPaymentMethod ?? SectionPaymentMethod.token,
      expectedStudents,
      sectionStartDate,
      sectionEndDate,
    }

    if (selectedProduct) {
      const sectionId = await createSectionWithProduct(this.repository, {
        ...baseParams,
        instructorUserId: baseParams.userId,
        currentUserId: this.repository.uid,
        productId: selectedProduct.productId,
        customProduct: selectedProduct.isCustom,
      })

      return sectionId
    }

    const doc = await createSection(this.repository, baseParams)

    return doc.id
  }

  redeemPromotions = async (
    sectionId: string,
    userPromotions: UserPromotion[]
  ) => {
    await Promise.all(
      userPromotions.map((userPromotion) =>
        redeemPromotions(this.repository, {
          userId: userPromotion.data.userId,
          promotionId: userPromotion.data.promotionId,
          userPromotionId: userPromotion.id,
          sectionId,
        })
      )
    )
    return
  }

  applyApprovedFreeAdminPromotion = async ({
    userId,
    sectionId,
  }: {
    sectionId: string
    userId: string
  }) => {
    return await applyApprovedFreeAdminPromotion(this.repository, {
      userId,
      sectionId,
    })
  }

  async retireAsAssistantorCollaborator(instructorUserId: string) {
    const isAssistant = this.impersonatedBreakoutUser.usersIAssist.value!.some(
      (i) => i.id === instructorUserId
    )
    const isCollaborator =
      !isAssistant &&
      this.impersonatedBreakoutUser.usersICollaborateWith.value!.some(
        (i) => i.id === instructorUserId
      )

    if (isAssistant) {
      return deleteAssistantship(this.repository.firestore, {
        userId: this.impersonationInfo?.userId ?? this.userId,
        instructorUserId,
      })
    } else if (isCollaborator) {
      const sectionsForInstructor =
        this.mySections.value?.filter(
          (section) => section.data.instructorUserId === instructorUserId
        ) ?? []

      const batchUpdates = sectionsForInstructor.map((section) => ({
        sectionId: section.id,
        assistantUserId: this.impersonationInfo?.userId ?? this.userId,
        action: 'remove' as const,
      }))

      if (batchUpdates.length > 0) {
        return batchUpdateSectionCollaborators(this.repository, batchUpdates)
      }
    }
  }

  private arraysEqual(arr1: unknown[], arr2: unknown[]) {
    if (arr1.length !== arr2.length) return false
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false
    }
    return true
  }

  startSectionPromotionStreams(sections: Section[]): void {
    for (const section of sections) {
      this.startSectionPromotionStream(section)
    }
  }

  startSectionPromotionStream(section: Section, retryCount = 0): void {
    const name = `section-promotions-${section.id}`
    if (this.hasStream(name)) return
    this.addStream(
      getSectionPromotions(this.repository, {
        sectionId: section.id,
      }),
      (promotions) => {
        this.promotionsForSection.set(section.id, promotions)
      },
      {
        name: name,
        disableCaptureException: true,
        onError: (error) => {
          if (retryCount < 5) {
            setTimeout(() => {
              this.startSectionPromotionStream(section, retryCount + 1)
            }, 400 * retryCount)
          } else {
            captureException(error)
          }
        },
      }
    )
  }

  changeSortingStrategy(
    strategy: InstructorClassesSortingStrategy,
    order: InstructorClassesSortingOrder
  ) {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  async deleteSection(sectionId: string) {
    await deleteSection(this.repository, sectionId)
  }

  async closeSection(sectionId: string) {
    await setSectionState(this.repository, {
      sectionId,
      sectionState: SectionState.completed,
    })
  }

  updateSection =
    (sectionId: string) =>
    async ({
      className,
      sectionName,
      organizationId,
      sectionPaymentMethod,
      sectionPassPrice,
      expectedStudents,
      sectionStartDate,
      sectionEndDate,
      selectedProduct,
    }: {
      className?: string
      sectionName?: string
      organizationId?: string
      sectionPaymentMethod?: SectionPaymentMethod
      sectionPassPrice?: number
      expectedStudents?: number
      sectionStartDate?: DateTime
      sectionEndDate?: DateTime
      selectedProduct?: { productId: string; isCustom: boolean }
    }) => {
      await updateSection(this.repository, sectionId, {
        className,
        sectionName,
        organizationId,
        sectionPaymentMethod,
        sectionPassPrice,
        expectedStudents,
        sectionStartDate,
        sectionEndDate,
      })

      if (selectedProduct?.productId) {
        await createSectionProduct(this.repository, {
          productId: selectedProduct.productId,
          sectionId,
          customProduct: selectedProduct.isCustom,
          currentUserId: this.repository.uid,
        })
      }

      return sectionId
    }

  createStudentInvitation =
    (sectionId: string) =>
    async ({ type }: { type: InvitationType }) => {
      const doc = await createStudentInvitation(this.repository, {
        sectionId,
        type,
      })

      return doc.id
    }
}
